import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThreeDots } from 'react-loader-spinner';
import ReactPaginate from 'react-paginate';
import moment from 'moment';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SearchIcon from '@mui/icons-material/Search';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { API_BASE_URL } from '../../config/api';

export default function PendingProjects() {
    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [count, setCount] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const fetchData = async () => {
        setLoading(true);

        const url = API_BASE_URL;
        const token = localStorage.getItem('authToken');
        const requestOptions = {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        };

        try {
            const response = await fetch(
                url + `/sub_cont_pending_project?page_num_start=1&page_size=20`,
                requestOptions
            );

            if (response.ok) {
                const data = await response.json();

                if (data?.data?.projects.length > 0) {
                    setCount(Math.ceil(data.data.projectsCount / 10));
                    setPageCount(data.data.projectsCount);
                    setProjects(data.data.projects);
                } else {
                    setError(true);
                }
            } else {
                setError(true);
            }
            setLoading(false);
        } catch (error) {
            setError(true);
            setLoading(false);
        }
    };

    useEffect(() => {
        setLoading(true);
        fetchData();
    }, []);

    const fetchPaginatedData = async (currentPage) => {
        const url = API_BASE_URL;
        setLoading(true);
        const token = localStorage.getItem('authToken');
        const requestOptions = {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        };
        const res = await fetch(
            url + `/sub_cont_pending_project?page_num_start=${currentPage}&page_size=20`,
            requestOptions
        );
        const data = await res.json();
        setLoading(false);
        return data;
    };

    const handlePageClick = async (data) => {
        const currentPage = data.selected + 1;
        const dataFromServer = await fetchPaginatedData(currentPage);
        setProjects(dataFromServer?.data?.projects);
    };

    return (
        <div className="profile_page">
            <div className="container">
                <div className="profile_section">
                    <div className="section_header">
                        <div className="section_header_row">
                            <div>
                                <h3>Pending Projects</h3>
                                <p>{pageCount} pending bid{pageCount !== 1 ? 's' : ''}</p>
                            </div>
                        </div>
                    </div>
                    <div className="section_content">
                        {loading ? (
                            <div className="loading_state">
                                <ThreeDots
                                    height="60"
                                    width="80"
                                    radius="9"
                                    color="#B1903F"
                                    ariaLabel="three-dots-loading"
                                    visible={true}
                                />
                                <p>Loading pending projects...</p>
                            </div>
                        ) : projects.length === 0 ? (
                            <div className="empty_state">
                                <HourglassEmptyIcon className="empty_icon" />
                                <h4>No Pending Bids</h4>
                                <p>You don't have any pending bids yet. Find a project and submit a bid to get started.</p>
                            </div>
                        ) : (
                            <>
                                <div className="projects_grid">
                                    {projects.map((project, index) => (
                                        <div 
                                            key={index} 
                                            className="project_card"
                                            onClick={() => navigate(`/project-details/${project.id}/true?pending=true`)}
                                        >
                                            <div className="project_card_header">
                                                <h3 className="project_title">
                                                    {project.project_name.length > 60 
                                                        ? `${project.project_name.slice(0, 60)}...` 
                                                        : project.project_name}
                                                </h3>
                                                <span className="project_status_badge pending">
                                                    <HourglassEmptyIcon /> Pending
                                                </span>
                                            </div>
                                            
                                            <div className="project_card_meta">
                                                {project?.my_bid && (
                                                    <span className="meta_item">
                                                        <CalendarTodayIcon />
                                                        Bid submitted {moment(project.my_bid.created_at).format("MMM DD, YYYY")}
                                                    </span>
                                                )}
                                                {project?.my_bid?.bid_amount && (
                                                    <span className="meta_item">
                                                        <AttachMoneyIcon />
                                                        ${Number(project.my_bid.bid_amount).toLocaleString()}
                                                    </span>
                                                )}
                                            </div>
                                            
                                            <p className="project_description">
                                                {project.project_description.length > 150 
                                                    ? `${project.project_description.slice(0, 150)}...` 
                                                    : project.project_description}
                                            </p>
                                        </div>
                                    ))}
                                </div>

                                {pageCount > 10 && (
                                    <div className="pagination_wrapper">
                                        <ReactPaginate
                                            previousLabel={"Prev"}
                                            nextLabel={"Next"}
                                            breakLabel={"..."}
                                            pageCount={count}
                                            marginPagesDisplayed={2}
                                            pageRangeDisplayed={3}
                                            onPageChange={handlePageClick}
                                            containerClassName={"pagination"}
                                            pageClassName={"page-item"}
                                            pageLinkClassName={"item-link"}
                                            previousClassName={"page-item"}
                                            previousLinkClassName={"item-link"}
                                            nextClassName={"page-item"}
                                            nextLinkClassName={"item-link"}
                                            breakClassName={"page-item"}
                                            breakLinkClassName={"item-link"}
                                            activeClassName={"active"}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div className="profile_actions centered">
                    <div className="search_input_wrapper large">
                        <SearchIcon className="search_icon" />
                        <input
                            type="text"
                            placeholder="Find a Project"
                            className="contacts_search_input"
                            onClick={() => navigate('/find-a-project')}
                            readOnly
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
